// Function index:
//   RoadSegment                        — one drawable centre-to-centre segment.
//   roadSegments(roads, knownKeys)     — consecutive path pairs where BOTH
//       hexes are known to the client (fog of war holds for free).
//   drawRoads(g, segments, discoveredKeys, toPixel) — repaint the road layer;
//       segments with an undiscovered end draw dim, discovered draw solid.
//
// No runtime imports on purpose (pixi is type-only; the hex→pixel projection
// is injected) so the module stays unit-testable under happy-dom.

import type { Graphics } from 'pixi.js';
import type { RoadInput } from './types.js';

export interface RoadSegment { a: [number, number]; b: [number, number] }

const ROAD_COLOUR = 0x9a7b4f; // packed-dirt tan
const ROAD_WIDTH = 5;
const ROAD_ALPHA = 0.8;
const ROAD_FOG_ALPHA = 0.3;

export function roadSegments(roads: RoadInput[], knownKeys: ReadonlySet<string>): RoadSegment[] {
  const out: RoadSegment[] = [];
  for (const road of roads) {
    for (let i = 0; i + 1 < road.path.length; i++) {
      const a = road.path[i]!;
      const b = road.path[i + 1]!;
      if (knownKeys.has(`${a[0]},${a[1]}`) && knownKeys.has(`${b[0]},${b[1]}`)) out.push({ a, b });
    }
  }
  return out;
}

export function drawRoads(
  g: Graphics,
  segments: RoadSegment[],
  discoveredKeys: ReadonlySet<string>,
  toPixel: (q: number, r: number) => { x: number; y: number },
): void {
  g.clear();
  for (const s of segments) {
    const pa = toPixel(s.a[0], s.a[1]);
    const pb = toPixel(s.b[0], s.b[1]);
    const dimmed = !discoveredKeys.has(`${s.a[0]},${s.a[1]}`) || !discoveredKeys.has(`${s.b[0]},${s.b[1]}`);
    g.moveTo(pa.x, pa.y);
    g.lineTo(pb.x, pb.y);
    g.stroke({ color: ROAD_COLOUR, width: ROAD_WIDTH, alpha: dimmed ? ROAD_FOG_ALPHA : ROAD_ALPHA, cap: 'round' });
  }
}
